import { useRef, useEffect } from "react";
import { EMPTY_STRING, systemUserConstants } from "../../constants";

export const useColumnSizingPersistence = ({
  tableId,
  columnSizing,
  user,
  store,
  updateUserInformation,
  enabled = true,
  debounceMs = 1000,
}) => {
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const lastSavedValueRef = useRef(null);

  useEffect(() => {
    if (!tableId || !enabled || !user?.id) return;
    if (!columnSizing || Object.keys(columnSizing).length === 0) return;

    const columnSizingStr = JSON.stringify(columnSizing);

    // first render (hydration)
    if (lastSavedValueRef.current === null) {
      lastSavedValueRef.current = columnSizingStr;
      return;
    }

    // skip duplicate
    if (lastSavedValueRef.current === columnSizingStr) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (isSavingRef.current) return;

      const existingColumnSizing =
        user?.userPreferences?.tables?.[tableId]?.columnSizing || {};

      if (JSON.stringify(existingColumnSizing) === columnSizingStr) {
        lastSavedValueRef.current = columnSizingStr;
        return;
      }

      isSavingRef.current = true;

      const dataToSend = {
        id: user?.id,
        role: user?.role,
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
        logo: user?.logo,
        companyId: user?.companyId,
        companyUserId: user?.companyUserId,
        stores: [
          {
            id: store?.currentStore?.id,
            name: store?.currentStore?.storeName,
          },
        ],
        password: EMPTY_STRING,
        userPreferences: {
          ...user?.userPreferences,
          tables: {
            ...user?.userPreferences?.tables,
            [tableId]: {
              ...user?.userPreferences?.tables?.[tableId],
              columnSizing,
            },
          },
        },
      };

      updateUserInformation(
        dataToSend,
        user?.companyId === systemUserConstants.ADMIN_SYSTEMUSERS
      );
      // To prevent duplicate api calls on resizing we use set timeout with debounce to track saving state.
      setTimeout(() => {
        isSavingRef.current = false;
        lastSavedValueRef.current = columnSizingStr;
      }, debounceMs);

      saveTimeoutRef.current = null;
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    tableId,
    columnSizing,
    user,
    store,
    updateUserInformation,
    enabled,
    debounceMs,
  ]);
};
